<template>
  <ModalComponent
    v-if="isVisible"
    :submit-button-text="'Assign'"
    :submit="handleAssign"
    :submit-disabled="!selectedCompanyId || isProcessing"
    :hide-submit-button="false"
    size="lg"
    @close="handleClose"
    @set-force-toggle="handleSetForceToggle"
  >
    <template #title>
      Assign {{ selectedCertificates.length }} Certificate{{
        selectedCertificates.length !== 1 ? "s" : ""
      }}
      ({{ totalMwh }} MWh Total)
    </template>
    <template #body>
      <div class="mb-3">
        <CompanySearchDropdown v-model="selectedCompanyId" />
      </div>
      <div v-if="selectedCertificates.length > 0">
        <h6>Summary:</h6>
        <ul class="list-unstyled">
          <li v-for="cert in selectedCertificates" :key="cert.id" class="mb-1">
            - {{ cert.volume_mwh }} MWh {{ cert.technology ?? "Unknown" }}
            <span v-if="cert.region">({{ cert.region }})</span>
          </li>
        </ul>
      </div>
    </template>
  </ModalComponent>
</template>

<script setup lang="ts">
import ModalComponent from "@/components/common/ModalComponent.vue";
import CompanySearchDropdown from "@/components/common/CompanySearchDropdown.vue";
import type { UnknownCertificateResponse } from "./virtualPortfoliosApi";
import { virtualPortfoliosApi } from "./virtualPortfoliosApi";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { computed, ref } from "vue";

const props = defineProps<{
  selectedCertificates: UnknownCertificateResponse[];
  isVisible: boolean;
}>();

const emit = defineEmits<{
  assigned: [];
  close: [];
}>();

const { globalErrorHandler, globalSuccessHandler } = useGlobalHandlers();
const selectedCompanyId = ref<number | null>(null);
const isProcessing = ref(false);

function handleSetForceToggle(fn: (value: boolean) => void) {
  // Show modal immediately when mounted (v-if controls when component renders)
  fn(true);
}

const totalMwh = computed(() => {
  return props.selectedCertificates.reduce(
    (sum, cert) => sum + cert.volume_mwh,
    0
  );
});

async function handleAssign() {
  if (!selectedCompanyId.value || props.selectedCertificates.length === 0) {
    return;
  }

  try {
    isProcessing.value = true;
    const certificateIds = props.selectedCertificates.map((cert) => cert.id);
    await virtualPortfoliosApi.assignCertificates({
      certificate_ids: certificateIds,
      target_company_id: selectedCompanyId.value,
    });
    globalSuccessHandler(
      "Success",
      `Successfully assigned ${certificateIds.length} certificate(s)`
    );
    emit("assigned");
    handleClose();
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    isProcessing.value = false;
  }
}

function handleClose() {
  selectedCompanyId.value = null;
  emit("close");
}
</script>

<style scoped lang="scss"></style>
